import { Transition } from "@headlessui/react";
import React, { useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { triggerError, triggerSuccess } from "../../../components/alerts";
import InputFileGroup from "../../../components/elements/InputFileGroup";
import { updateStorysApi } from "../../../store/actions/storiesActions";
import { uploadImageApi } from "../../../store/actions/uploadActions";

const EditStoryModal = ({
  show,
  handleModalClose,
  data,
  handleUpdateData,
  ...props
}) => {
  const { addToast } = useToasts();

  const [formData, setFormData] = useState({
    name: "",
    companyRole: "",
    experience: "",
    storyLink: "",
    imageUrl: "",
  });

  const [images, setImages] = useState("");
  const [displayImages, setDisplayImages] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data?.name || "",
        companyRole: data?.companyRole || "",
        experience: data?.experience || "",
        storyLink: data?.storyLink || "",
        imageUrl: data?.imageUrl || "",
      });
      setDisplayImages(data?.imageUrl || "");
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (acceptedFiles) => {
    const file = acceptedFiles && acceptedFiles[0];
    const fileSize = file?.size;

    if (!file || fileSize > 1048576) {
      setError("Image must be 1MB or less.");
      setImages("");
      setDisplayImages(formData.imageUrl || "");
      return;
    }

    setError("");
    var reader = new FileReader();
    reader.onload = function () {
      setDisplayImages(reader.result);
    };
    setImages(acceptedFiles[0]);
    reader.readAsDataURL(acceptedFiles[0]);
  };

  const handleRemoveImage = () => {
    setImages("");
    setDisplayImages("");
    setFormData((prev) => ({ ...prev, imageUrl: "" }));
    setError("");
  };

  const handleSubmit = async () => {
    const { name, companyRole, experience, storyLink, imageUrl } = formData;

    if (!name || !companyRole || !experience) {
      setError("Please make sure all required fields are filled");
      return;
    }

    setError("");
    try {
      setLoading(true);

      let payload = {
        name,
        companyRole,
        experience,
      };

      if (storyLink) {
        payload["storyLink"] = storyLink;
      }

      // If user selected a new image, upload and use the returned url
      if (images) {
        const uploadImageResponse = await uploadImageApi(images);
        if (uploadImageResponse?.url) {
          payload["imageUrl"] = uploadImageResponse.url;
        }
      } else if (imageUrl) {
        // keep existing image if not removed and no new upload
        payload["imageUrl"] = imageUrl;
      }

      const response = await updateStorysApi(data._id, payload);
      triggerSuccess(
        addToast,
        response?.message || "Story updated successfully"
      );
      setLoading(false);

      if (typeof handleUpdateData === "function") {
        handleUpdateData(data._id, response?.data);
      }

      handleModalClose();
    } catch (error) {
      triggerError(addToast, error);
      setError(error?.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <Transition
          show={show}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-black opacity-50"></div>
          </div>
        </Transition>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div
          className="inline-block align-center bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div className="text-right">
            <button
              onClick={handleModalClose}
              className="mt-4 mr-8 px-3 py-1 rounded-full text-lg focus:outline-none bg-white shadow-lg text-secondary"
            >
              x
            </button>
          </div>
          <div className="bg-white px-5 py-1 sm:px-12 sm:py-6">
            <div className="">
              <h3
                className="mb-6 leading-6 text-lg md:text-xl text-primary font-bold"
                id="modal-headline"
              >
                Edit Story
              </h3>

              <div className="space-y-6">
                <div>
                  <p className="text-sm pb-2">Name</p>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full block h-12 px-4 story-form-input"
                  />
                </div>

                <div>
                  <p className="text-sm pb-2">Current role and company</p>
                  <input
                    type="text"
                    name="companyRole"
                    value={formData.companyRole}
                    onChange={handleChange}
                    className="w-full block h-12 px-4 story-form-input"
                    placeholder="e.g. Software Engineer at Google"
                  />
                </div>

                <div>
                  <p className="text-sm pb-2">
                    Story Link (e.g LinkedIn, X, Facebook, Instagram Post Link
                    etc)
                  </p>
                  <input
                    type="text"
                    name="storyLink"
                    value={formData.storyLink}
                    onChange={handleChange}
                    className="w-full block h-12 px-4 story-form-input"
                    placeholder="e.g. https://www.linkedin.com/posts/your-profile-id"
                  />
                </div>

                <div>
                  <p className="text-sm pb-2">Utiva experience</p>
                  <textarea
                    rows="6"
                    className="w-full block px-4 story-form-input resize-none py-4 text-sm-15"
                    onChange={handleChange}
                    name="experience"
                    value={formData.experience}
                  ></textarea>
                </div>

                <div>
                  <p className="text-sm pb-2 text-primary font-bold">
                    Replace image (optional)
                  </p>
                  <InputFileGroup
                    preview={displayImages || ""}
                    onDrop={handleImageChange}
                    accept=".jpg,.jpeg,.png"
                    onDeleteClick={() => handleRemoveImage()}
                    marginBottom="mb-0"
                    maxSize={1048576}
                  />
                </div>

                {error && <p className="text-sm-15 text-red-600">{error}</p>}

                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleSubmit}
                    className="py-3 px-10 bg-secondary text-white rounded-md my-3 focus:outline-none"
                  >
                    {loading ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStoryModal;
